/**
 * Use Term API
 *
 * API endpoints for use term management operations
 */

import dayjs from "dayjs";
import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import { saveTermArgsSchema } from "./schemas";
import type { DeleteTermArgs, GetTermsByCompanyArgs, SaveTermArgs, Term } from "./useTerm.types";

/**
 * Use Term API endpoints
 * Provides operations for use term management
 */
export const useTermApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get terms by company ID
     * @param id - Company ID
     * @returns Array of terms
     */
    getTermsByCompany: build.query<Term[], GetTermsByCompanyArgs>({
      query: ({ id }) => `/useTerm/company/${id}`,
      transformResponse: (response: Term[]) => {
        return response.map((term) => ({
          ...term,
          createdAt: dayjs(term.createdAt).format("DD/MM/YYYY - HH:mm"),
          updatedAt: term.updatedAt ? dayjs(term.updatedAt).format("DD/MM/YYYY - HH:mm") : null
        }));
      }
    }),

    /**
     * Get single term user by ID
     * @param id - Term ID
     * @returns Term details
     */
    getTermsUserByCompany: build.query<Term, Pick<Term, "id">>({
      query: ({ id }) => `/useTerm/user/company/${id}`
    }),

    /**
     * Save term (create or update)
     * @param payload - Term data to save
     * @returns void
     */
    saveTerm: build.mutation<unknown, SaveTermArgs>({
      query: (body) => ({
        url: "/useTerm/save",
        method: "POST",
        body
      }),
      argSchema: saveTermArgsSchema
    }),

    /**
     * Delete term
     * @param id - Term ID
     * @returns void
     */
    deleteTerm: build.mutation<unknown, DeleteTermArgs>({
      query: ({ id }) => ({
        url: `/useTerm/delete/${id}`,
        method: "DELETE"
      })
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
useTermApi.enhanceEndpoints({
  addTagTypes: ["UseTerm", "UseTermByCompany", "UseTermUserByCompany"],
  endpoints: {
    getTermsByCompany: {
      providesTags: (result, error, arg) => [{ type: "UseTermByCompany", id: arg.id }],
      onQueryStarted: async (_args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando termos...",
          success: "Termos carregados com sucesso!",
          error: "Erro ao carregar termos."
        });
      }
    },
    getTermsUserByCompany: {
      providesTags: (result, error, { id }) => [{ type: "UseTermUserByCompany", id }]
    },
    saveTerm: {
      invalidatesTags: ["UseTerm", "UseTermByCompany"],
      onQueryStarted: async (_args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Salvando termo...",
          success: "Termo salvo com sucesso!",
          error: "Erro ao salvar termo."
        });
      }
    },
    deleteTerm: {
      invalidatesTags: ["UseTerm", "UseTermByCompany"],
      onQueryStarted: async (_args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Deletando termo...",
          success: "Termo deletado com sucesso!",
          error: "Erro ao deletar termo."
        });
      }
    }
  }
});
